import {
  getStoredFlightFormDraft,
  saveFlightFormDraft,
  saveResolvedFlightInfo,
} from 'legacyfrontend/flights/search/flightSearchStorage';
import { EMPTY_FLIGHT_SEARCH, passengerAges, passengerCountsFromAges } from './flightSearchValues';
import type { FlightSearchValues } from './flightSearchValues';
import type { Airport } from './api/flights.types';

/**
 * Typed boundary over the flight search draft kept in session storage.
 *
 * The draft is a contract shared with the results page and the flight detail
 * context — both read and write it through `flightSearchStorage`. This module
 * deliberately reuses that storage instead of re-implementing it: a second copy
 * of the shape would drift and break flight selection state. Only the mapping
 * to and from this feature's typed values lives here.
 */

interface StoredAirport {
  id?: number | null;
  airportCode?: string;
  airportName?: string;
  cityCode?: string;
  cityName?: string;
  countryCode?: string;
}

interface StoredDraft {
  origin?: StoredAirport | null;
  destination?: StoredAirport | null;
  passengers?: number[];
  departureDate?: Date | string | null;
  returnDate?: Date | string | null;
  isOnlyOneWay?: boolean;
  currency?: string;
}

function toAirport(stored: StoredAirport | null | undefined): Airport | null {
  if (!stored?.cityCode || !String(stored.cityName || '').trim()) return null;

  return {
    airportCode: stored.airportCode,
    airportName: stored.airportName,
    cityCode: stored.cityCode,
    cityName: String(stored.cityName).trim(),
    countryCode: stored.countryCode,
    id: stored.id ?? null,
  };
}

function toDate(value: Date | string | null | undefined): Date | null {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** Returns the stored draft only when it is complete enough to prefill the form. */
export function readFlightSearchDraft(): FlightSearchValues | null {
  let stored: StoredDraft | null = null;

  try {
    stored = getStoredFlightFormDraft() as StoredDraft | null;
  } catch {
    return null;
  }

  const origin = toAirport(stored?.origin);
  const destination = toAirport(stored?.destination);
  const departureDate = toDate(stored?.departureDate);

  if (!origin || !destination || !departureDate) return null;

  const isOnlyOneWay = Boolean(stored?.isOnlyOneWay);

  return {
    currency: stored?.currency || EMPTY_FLIGHT_SEARCH.currency,
    departureDate,
    destination,
    isOnlyOneWay,
    origin,
    passengers: passengerCountsFromAges(stored?.passengers),
    returnDate: isOnlyOneWay ? null : toDate(stored?.returnDate),
  };
}

export function writeFlightSearchDraft(values: FlightSearchValues): void {
  try {
    saveFlightFormDraft({
      currency: values.currency,
      departureDate: values.departureDate,
      destination: values.destination,
      isOnlyOneWay: values.isOnlyOneWay,
      origin: values.origin,
      passengers: passengerAges(values.passengers),
      returnDate: values.returnDate,
    });
  } catch {
    // A full or blocked sessionStorage must never stop a search.
  }
}

/**
 * Records the resolved airport metadata for one endpoint.
 *
 * The booking flow decides whether a trip is international by comparing
 * `originCountryCode` with `destinationCountryCode` in this state, so these keys
 * have to be written on selection exactly as the legacy filter form wrote them.
 */
export function saveResolvedAirport(field: 'origin' | 'destination', airport: Airport): void {
  const payload =
    field === 'origin'
      ? {
          originAirportCode: airport.airportCode,
          originAirportName: airport.airportName,
          originCityCode: airport.cityCode,
          originCountryCode: airport.countryCode,
          originId: airport.id,
          originName: airport.cityName,
        }
      : {
          destinationAirportCode: airport.airportCode,
          destinationAirportName: airport.airportName,
          destinationCityCode: airport.cityCode,
          destinationCountryCode: airport.countryCode,
          destinationId: airport.id,
          destinationName: airport.cityName,
        };

  try {
    saveResolvedFlightInfo(payload);
  } catch {
    // Non-fatal: the search still works, booking just re-resolves later.
  }
}

/** Stable key used to avoid rewriting an unchanged draft on every keystroke. */
export function flightDraftKey(values: FlightSearchValues): string {
  return JSON.stringify({
    departureDate: values.departureDate?.getTime() ?? null,
    destination: values.destination?.cityCode ?? null,
    isOnlyOneWay: values.isOnlyOneWay,
    origin: values.origin?.cityCode ?? null,
    passengers: passengerAges(values.passengers).join(','),
    returnDate: values.returnDate?.getTime() ?? null,
  });
}
